import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  AlertCircle, 
  CheckCircle2, 
  Key, 
  Search, 
  X,
  Globe,
  ExternalLink,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from '@/lib/supabase';
import { Separator } from '@/components/ui/separator';
import IngramMicroIntegration, { IngramMicroIntegration as IngramMicroIntegrationType } from '@/components/integrations/IngramMicroIntegration';

// Interface para as integrações
interface Integration {
  id: string;
  name: string;
  description: string;
  logo: string;
  isActive: boolean;
  apiKey?: string;
  isConnected: boolean;
  available: boolean;
  dbId?: string; // ID do registro no banco de dados
}

// Lista de integrações disponíveis
const AVAILABLE_INTEGRATIONS: Integration[] = [
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'CRM, marketing e vendas',
    logo: 'https://cdn.worldvectorlogo.com/logos/hubspot-1.svg',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: true
  },
  {
    id: 'sendgrid',
    name: 'Sendgrid',
    description: 'Plataforma de email marketing',
    logo: 'https://cdn.worldvectorlogo.com/logos/sendgrid-1.svg',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: true
  },
  {
    id: 'brandfetch',
    name: 'Brandfetch',
    description: 'API de informações de marcas e logos',
    logo: 'https://brandfetch.com/favicon.ico',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: true
  },
  {
    id: 'ingrammicro',
    name: 'Ingram Micro',
    description: 'Distribuidor global de tecnologia',
    logo: 'https://www.ingrammicro.com/favicon.ico',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: true
  },
  {
    id: 'microsoft365',
    name: 'Microsoft 365',
    description: 'Suite de produtividade e colaboração',
    logo: 'https://cdn.worldvectorlogo.com/logos/office-365-1.svg',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: false
  },
  {
    id: 'microsoftteams',
    name: 'Microsoft Teams',
    description: 'Plataforma de comunicação e colaboração',
    logo: 'https://cdn.worldvectorlogo.com/logos/microsoft-teams-1.svg',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: false
  },
  {
    id: 'omie',
    name: 'Omie',
    description: 'Sistema de gestão empresarial',
    logo: 'https://www.omie.com.br/wp-content/uploads/2021/06/cropped-favicon-32x32.png',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: false
  },
  {
    id: 'learnworlds',
    name: 'Learnworlds',
    description: 'Plataforma de cursos online',
    logo: 'https://www.learnworlds.com/favicon.ico',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: false
  },
  {
    id: 'trengo',
    name: 'Trengo',
    description: 'Plataforma de comunicação unificada',
    logo: 'https://trengo.com/favicon.ico',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: false
  },
  {
    id: 'plumsail',
    name: 'Plumsail',
    description: 'Automação de documentos e processos',
    logo: 'https://plumsail.com/favicon.ico',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: false
  },
  {
    id: 'simplybook',
    name: 'Simply Book',
    description: 'Sistema de agendamento online',
    logo: 'https://simplybook.me/favicon.ico',
    isActive: false,
    apiKey: '',
    isConnected: false,
    available: false
  }
];

const Integrations = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [testDomain, setTestDomain] = useState('');
  const [isTestingBrandfetch, setIsTestingBrandfetch] = useState(false);
  const [brandfetchResult, setBrandfetchResult] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showSubscriptionKey, setShowSubscriptionKey] = useState(false);
  const [ingramMicroCredentials, setIngramMicroCredentials] = useState({
    username: '',
    password: '',
    subscriptionKey: ''
  });

  // Add Brandfetch specific state variables
  const [brandfetchApiKey, setBrandfetchApiKey] = useState('');
  const [brandfetchTestDomain, setBrandfetchTestDomain] = useState('');
  const [testingBrandfetch, setTestingBrandfetch] = useState(false);
  const [brandfetchTestResult, setBrandfetchTestResult] = useState<any>(null);

  useEffect(() => {
    // Carregar integrações e verificar quais já estão ativas no banco de dados
    const loadIntegrations = async () => {
      setIsLoading(true);
      try {
        // Inicializar com as integrações disponíveis
        const initialIntegrations = [...AVAILABLE_INTEGRATIONS];
        
        // Buscar chaves de API ativas no banco de dados
        const { data: apiKeys, error } = await supabase
          .from('api_keys')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          throw error;
        }
        
        // Atualizar o estado das integrações com base nos dados do banco
        if (apiKeys && apiKeys.length > 0) {
          apiKeys.forEach(apiKey => {
            const integrationIndex = initialIntegrations.findIndex(
              i => i.id.toLowerCase() === apiKey.integration_name.toLowerCase()
            );
            
            if (integrationIndex !== -1) {
              initialIntegrations[integrationIndex] = {
                ...initialIntegrations[integrationIndex],
                isActive: apiKey.is_active,
                apiKey: apiKey.api_key,
                isConnected: apiKey.is_active,
                dbId: apiKey.id
              };
            }
          });
        }
        
        setIntegrations(initialIntegrations);
      } catch (error) {
        console.error('Erro ao carregar integrações:', error);
        toast.error('Erro ao carregar integrações do banco de dados');
        // Fallback para as integrações locais
        setIntegrations(AVAILABLE_INTEGRATIONS);
      } finally {
        setIsLoading(false);
      }
    };

    loadIntegrations();
  }, []);

  const handleToggleIntegration = (id: string) => {
    const integration = integrations.find(i => i.id === id);
    
    if (integration) {
      // Verificar se a integração está disponível
      if (!integration.available) {
        toast.error(`A integração com ${integration.name} estará disponível em breve.`);
        return;
      }
      
      if (!integration.isActive) {
        // Se estiver ativando, abrir diálogo para API key
        setSelectedIntegration(integration);
        setApiKeyInput(integration.apiKey || '');
        setIsDialogOpen(true);
      } else {
        // Se estiver desativando, apenas atualizar o estado
        updateIntegrationStatus(id, false, integration.apiKey || '');
      }
    }
  };

  const updateIntegrationStatus = async (id: string, isActive: boolean, apiKey: string = '') => {
    try {
      const integration = integrations.find(i => i.id === id);
      if (!integration) return;

      // Se já existe um registro no banco de dados, atualizar
      if (integration.dbId) {
        const { error } = await supabase
          .from('api_keys')
          .update({ 
            is_active: isActive,
            updated_at: new Date().toISOString()
          })
          .eq('id', integration.dbId);

        if (error) throw error;
      } 
      // Se não existe e está ativando, criar novo registro
      else if (isActive) {
        const { data, error } = await supabase
          .from('api_keys')
          .insert({
            integration_name: integration.id,
            api_key: apiKey,
            is_active: true
          })
          .select('id')
          .single();

        if (error) throw error;
        
        // Atualizar o ID do banco de dados na integração
        integration.dbId = data.id;
      }

      // Atualizar o estado local
      setIntegrations(prevIntegrations =>
        prevIntegrations.map(item =>
          item.id === id
            ? { 
                ...item, 
                isActive, 
                apiKey: isActive ? apiKey : item.apiKey,
                isConnected: isActive && apiKey.trim() !== '',
                dbId: integration.dbId
              }
            : item
        )
      );
      
      toast.success(`Integração ${isActive ? 'ativada' : 'desativada'}: ${integration.name}`);
    } catch (error) {
      console.error('Erro ao atualizar integração:', error);
      toast.error(`Erro ao ${isActive ? 'ativar' : 'desativar'} integração. Tente novamente.`);
    }
  };

  // Função para obter instruções específicas para cada integração
  const getIntegrationInstructions = (integrationId: string) => {
    switch (integrationId) {
      case 'hubspot':
        return {
          instructions: 'Você pode encontrar sua API Key no painel de desenvolvedores do HubSpot.',
          link: 'https://developers.hubspot.com/docs/api/overview'
        };
      case 'sendgrid':
        return {
          instructions: 'Acesse o painel do Sendgrid, vá para Settings > API Keys para gerar uma nova chave.',
          link: 'https://app.sendgrid.com/settings/api_keys'
        };
      case 'brandfetch':
        return {
          instructions: 'Crie uma conta no Brandfetch e obtenha sua API Key no painel de desenvolvedores.',
          link: 'https://developers.brandfetch.com/'
        };
      default:
        return {
          instructions: 'Você pode encontrar sua API Key no painel de desenvolvedores.',
          link: ''
        };
    }
  };

  // Função para testar a API do Brandfetch
  const testBrandfetchAPI = async () => {
    if (!brandfetchTestDomain || !brandfetchApiKey) {
      toast.error("Domínio e chave API são obrigatórios para testar");
      return;
    }

    // Show loading state
    setTestingBrandfetch(true);
    
    try {
      // Make a test request to the Brandfetch API
      const response = await fetch(`https://api.brandfetch.io/v2/brands/${brandfetchTestDomain}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${brandfetchApiKey}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("API Brandfetch funcionando corretamente!");
        setBrandfetchTestResult(data);
      } else {
        const errorData = await response.json();
        toast.error(`Erro ao testar API: ${errorData.message || 'Falha na conexão'}`);
        setBrandfetchTestResult(null);
      }
    } catch (error) {
      toast.error(`Erro ao testar API: ${error instanceof Error ? error.message : 'Falha na conexão'}`);
      setBrandfetchTestResult(null);
    } finally {
      setTestingBrandfetch(false);
    }
  };

  // Add a function to handle saving Ingram Micro credentials
  const saveIngramMicroCredentials = async (id: string, credentials: any) => {
    try {
      const integration = integrations.find(i => i.id === id);
      if (!integration) return;

      // Check if credentials are valid
      if (!credentials.username || !credentials.api_secret || !credentials.api_key) {
        toast.error('Todas as credenciais são obrigatórias');
        return;
      }

      // If already exists in database, update
      if (integration.dbId) {
        const { error } = await supabase
          .from('api_keys')
          .update({
            api_key: credentials.api_key,
            api_secret: credentials.api_secret,
            username: credentials.username,
            is_active: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', integration.dbId);

        if (error) throw error;
      } else {
        // Otherwise, insert new record
        const { data, error } = await supabase
          .from('api_keys')
          .insert({
            integration_name: id,
            api_key: credentials.api_key,
            api_secret: credentials.api_secret,
            username: credentials.username,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select();

        if (error) throw error;
        
        // Update local state with new dbId
        if (data && data.length > 0) {
          setIntegrations(prevIntegrations => 
            prevIntegrations.map(i => 
              i.id === id ? { ...i, dbId: data[0].id } : i
            )
          );
        }
      }

      // Update local state
      setIntegrations(prevIntegrations => 
        prevIntegrations.map(i => 
          i.id === id ? { 
            ...i, 
            isActive: true, 
            isConnected: true,
            apiKey: credentials.api_key // Store the API key for display purposes
          } : i
        )
      );

      toast.success('Credenciais da Ingram Micro salvas com sucesso!');
    } catch (error) {
      console.error('Error saving Ingram Micro credentials:', error);
      toast.error('Erro ao salvar credenciais da Ingram Micro');
    }
  };

  // Filter integrations based on search query
  const filteredIntegrations = integrations.filter(
    integration => integration.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group integrations by availability
  const availableIntegrations = filteredIntegrations.filter(i => i.available);
  const comingSoonIntegrations = filteredIntegrations.filter(i => !i.available);

  // Create a mock integration object for IngramMicroIntegration
  const getIngramMicroIntegrationObject = (): IngramMicroIntegrationType => {
    const integration = integrations.find(i => i.id === 'ingrammicro');
    return {
      id: 'ingrammicro',
      name: 'Ingram Micro',
      description: 'Distribuidor global de tecnologia',
      logo: 'https://www.ingrammicro.com/favicon.ico',
      isActive: integration?.isActive || false,
      credentials: {
        username: ingramMicroCredentials.username,
        password: ingramMicroCredentials.password,
        subscriptionKey: ingramMicroCredentials.subscriptionKey
      },
      status: integration?.isConnected ? 'connected' : 'disconnected',
      dbId: integration?.dbId
    };
  };

  // Update the onSave handler for IngramMicroIntegration
  const handleSaveIngramMicroIntegration = async (id: string, credentials: any) => {
    // Save credentials as JSON string
    const apiKeyJson = JSON.stringify(credentials);
    
    // Update the integration
    await updateIntegrationStatus(id, true, apiKeyJson);
    
    // Close the dialog
    setIsDialogOpen(false);
  };

  const saveIntegration = async () => {
    if (!selectedIntegration) return;
    
    let apiKeyToUse = apiKeyInput;
    
    // Handle different integration types
    if (selectedIntegration.id === 'brandfetch') {
      apiKeyToUse = brandfetchApiKey;
      
      // Validate required fields
      if (!apiKeyToUse) {
        toast.error("Chave de API é obrigatória");
        return;
      }
    } else if (selectedIntegration.id === 'ingrammicro') {
      // Validation already handled in handleSaveIngramMicroIntegration
      return;
    }
    
    // Save API key to database
    const apiKeyJson = JSON.stringify({ apiKey: apiKeyToUse });
    await updateIntegrationStatus(selectedIntegration.id, true, apiKeyJson);
    
    // Reset dialog state
    setIsDialogOpen(false);
    setBrandfetchResult(null);
    setBrandfetchTestResult(null);
    setApiKeyInput('');
    setBrandfetchApiKey('');
    setBrandfetchTestDomain('');
    setTestDomain('');
    
    toast.success(`Integração com ${selectedIntegration.name} ativada com sucesso!`);
  };

  return (
    <div className="container mx-auto py-6">
      <Helmet>
        <title>CSM | Trivium - Integrações</title>
      </Helmet>
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-extrabold">Integrações</h1>
          <p className="text-muted-foreground">
            Configure integrações com sistemas e serviços externos
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Gerenciamento de Integrações</CardTitle>
          <CardDescription>
            Ative e configure integrações com sistemas externos para potencializar seus processos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar integrações..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="py-12 flex justify-center">
              <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {availableIntegrations.length > 0 && (
                <div>
                  <h3 className="text-lg font-extrabold mb-4">Integrações Disponíveis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableIntegrations.map(integration => (
                      <Card key={integration.id} className="overflow-hidden">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-3">
                              <img 
                                src={integration.logo} 
                                alt={integration.name} 
                                className="h-8 w-8 rounded-sm object-contain" 
                              />
                              <CardTitle className="text-lg">{integration.name}</CardTitle>
                            </div>
                            <Switch 
                              checked={integration.isActive}
                              onCheckedChange={() => handleToggleIntegration(integration.id)}
                            />
                          </div>
                          <CardDescription className="line-clamp-2">
                            {integration.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="pb-2">
                          {integration.isActive && integration.isConnected && (
                            <Badge variant="outline" className="flex items-center w-fit bg-green-50">
                              <CheckCircle2 className="mr-1 h-3 w-3 text-green-600" />
                              <span className="text-green-600">Conectado</span>
                            </Badge>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {comingSoonIntegrations.length > 0 && (
                <div>
                  <h3 className="text-lg font-extrabold mb-4">Em Breve</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {comingSoonIntegrations.map(integration => (
                      <Card key={integration.id} className="overflow-hidden opacity-70">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-3">
                              <img 
                                src={integration.logo} 
                                alt={integration.name} 
                                className="h-8 w-8 rounded-sm object-contain grayscale" 
                              />
                              <CardTitle className="text-lg">{integration.name}</CardTitle>
                            </div>
                            <Badge variant="outline">Em Breve</Badge>
                          </div>
                          <CardDescription className="line-clamp-2">
                            {integration.description}
                          </CardDescription>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Integration Configuration Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Configurar {selectedIntegration?.name}</DialogTitle>
            <DialogDescription>
              Insira as credenciais para conectar com {selectedIntegration?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {selectedIntegration?.id === 'ingrammicro' ? (
              <IngramMicroIntegration 
                integration={getIngramMicroIntegrationObject()}
                onToggle={(id) => handleToggleIntegration(id)}
                onSave={handleSaveIngramMicroIntegration}
              />
            ) : selectedIntegration?.id === 'brandfetch' ? (
              <div>
                <div className="mb-8">
                  <h2 className="text-lg font-extrabold mb-2">Configuração da API Brandfetch</h2>
                  <p className="text-sm text-gray-500 mb-4">
                    Integre com a API Brandfetch para obter informações de marca das empresas automaticamente.
                    Você precisará de uma chave de API da Brandfetch, que pode ser obtida em{' '}
                    <a 
                      href="https://www.brandfetch.com/developers"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      brandfetch.com/developers
                    </a>.
                  </p>
                  
                  <div className="flex flex-col space-y-4">
                    <div>
                      <Label htmlFor="brandfetch-api-key">API Key</Label>
                      <Input
                        id="brandfetch-api-key"
                        type="password" 
                        value={brandfetchApiKey}
                        onChange={(e) => setBrandfetchApiKey(e.target.value)}
                        placeholder="Insira sua chave API da Brandfetch"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="brandfetch-test-domain">Testar com domínio</Label>
                      <Input
                        id="brandfetch-test-domain"
                        type="text"
                        value={brandfetchTestDomain}
                        onChange={(e) => setBrandfetchTestDomain(e.target.value)}
                        placeholder="exemplo.com"
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        disabled={testingBrandfetch || !brandfetchApiKey}
                        onClick={() => toast.info("Definições salvas.")}
                        className="w-1/2"
                      >
                        Salvar configurações
                      </Button>
                      <Button
                        disabled={!brandfetchTestDomain || testingBrandfetch || !brandfetchApiKey}
                        onClick={testBrandfetchAPI}
                        className="w-1/2"
                      >
                        {testingBrandfetch ? "Testando..." : "Testar"}
                      </Button>
                    </div>
                  </div>
                  
                  {brandfetchTestResult && (
                    <div className="mt-6 border rounded-md p-4">
                      <h3 className="text-md font-extrabold mb-2">Resultado do teste</h3>
                      {brandfetchTestResult.error ? (
                        <div className="text-red-500 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          {brandfetchTestResult.error}
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          {brandfetchTestResult.icon && (
                            <div className="h-10 w-10 rounded overflow-hidden">
                              <img src={brandfetchTestResult.icon} alt="Brand logo" className="h-full w-full object-contain" />
                            </div>
                          )}
                          <span className="font-extrabold">{brandfetchTestResult.name}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="apiKey">Chave de API</Label>
                  <div className="flex space-x-2">
                    <Input 
                      id="apiKey" 
                      value={apiKeyInput}
                      onChange={(e) => setApiKeyInput(e.target.value)}
                      placeholder="Insira sua chave de API"
                      type={showPassword ? "text" : "password"}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                {selectedIntegration?.id === 'hubspot' && (
                  <p className="text-sm text-muted-foreground">
                    Obtenha sua chave API em{" "}
                    <a 
                      href="https://app.hubspot.com/api-key" 
                      target="_blank" 
                      rel="noreferrer"
                      className="text-primary hover:underline inline-flex items-center"
                    >
                      app.hubspot.com/api-key
                      <ExternalLink className="ml-1 h-3 w-3" />
                    </a>
                  </p>
                )}
                {selectedIntegration?.id === 'sendgrid' && (
                  <p className="text-sm text-muted-foreground">
                    Obtenha sua chave API em{" "}
                    <a 
                      href="https://app.sendgrid.com/settings/api_keys" 
                      target="_blank" 
                      rel="noreferrer"
                      className="text-primary hover:underline inline-flex items-center"
                    >
                      app.sendgrid.com/settings/api_keys
                      <ExternalLink className="ml-1 h-3 w-3" />
                    </a>
                  </p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsDialogOpen(false);
              setApiKeyInput('');
              setSelectedIntegration(null);
              setTestDomain('');
              setBrandfetchResult(null);
              setShowPassword(false);
              setIngramMicroCredentials({
                username: '',
                password: '',
                subscriptionKey: ''
              });
            }}>
              Cancelar
            </Button>
            
            {selectedIntegration?.id !== 'ingrammicro' && (
              <Button 
                onClick={saveIntegration}
                disabled={selectedIntegration?.id === 'brandfetch' 
                  ? (!brandfetchApiKey || (brandfetchTestResult?.error && brandfetchTestResult))
                  : !apiKeyInput
                }
              >
                <Key className="mr-2 h-4 w-4" />
                Salvar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Integrations; 